import type { PersonaId } from '@evi/shared';
import type { Column } from '@/components/ui/ResponsiveTable';

/**
 * Persona-aware column definition. Extends ResponsiveTable's
 * Column<R> with optional `visibleFor: PersonaId[]` — when unset
 * the column is visible to every persona; when set, only the
 * listed personas see it.
 *
 * Designed to plug into ResponsiveTable without an adapter:
 * `columnsForPersona` filters the array AND strips `visibleFor`
 * from the returned columns so the result is a plain
 * `Column<R>[]` ready to hand to <ResponsiveTable columns={...} />.
 *
 * First use is the Building Admin Reports Booking History tab,
 * where the Tenant column is `visibleFor: ['super_admin']` —
 * tenant_admin / building_admin see 7 columns; super_admin sees 8
 * (when they eventually consume the same tab).
 *
 * Decision: this helper is admin-local (apps/admin/src/lib/) rather
 * than promoted to @evi/ui. Same reasoning as ScopedReportsPage —
 * single-app consumer today, promotion is a future concern when a
 * second app (PWA?) needs the pattern.
 */
export interface PersonaColumn<R> extends Column<R> {
  /** Personas this column is visible to. When unset, visible to
   *  every persona. When set, only the listed personas see it. */
  visibleFor?: ReadonlyArray<PersonaId>;
}

export function columnsForPersona<R>(
  columns: ReadonlyArray<PersonaColumn<R>>,
  persona: PersonaId | null,
): Column<R>[] {
  return columns
    .filter((col) => {
      if (!col.visibleFor) return true;
      if (persona === null) return false;
      return col.visibleFor.includes(persona);
    })
    .map((col) => {
      // Strip the meta key so the returned shape is a plain Column<R>
      // that ResponsiveTable consumes without seeing internal config.
      const { visibleFor: _vf, ...rest } = col;
      return rest;
    });
}
